import React, { useState } from "react";
import { Bot, Loader2, ClipboardList, CheckCircle2, AlertTriangle, Target, FileQuestion, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { tierConfig } from "./AIScoreBadge";

/**
 * Carte synthétique du verdict IA sur le questionnaire conditionnel.
 *
 * Props :
 *   analysis    : objet questionnaire_analysis (peut être null)
 *   onAnalyze   : async () => void  (déclenche /analyze-questionnaire)
 *   loading     : bool
 *   compact     : true → version compacte (utilisée dans Dialog ou Aperçu)
 */
const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso.slice(0, 16); }
};

export default function QuestionnaireAnalysisCard({ analysis, onAnalyze, loading = false, compact = false, testId = "qst-analysis-card" }) {
  const [running, setRunning] = useState(false);
  const handle = async () => {
    if (running || loading) return;
    setRunning(true);
    try { await onAnalyze?.(); } finally { setRunning(false); }
  };
  const isRunning = running || loading;

  if (!analysis) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 bg-[#14101c]/60 p-5 text-center" data-testid={`${testId}-empty`}>
        <FileQuestion className="w-8 h-8 text-violet-400 mx-auto mb-3" />
        <h3 className="text-sm font-bold text-white mb-1">Analyse du questionnaire non générée</h3>
        <p className="text-xs text-slate-400 mb-4">
          Lancez une analyse IA pour qualifier ce prospect d'après ses réponses au questionnaire conditionnel.
        </p>
        <Button onClick={handle} disabled={isRunning} className="bg-violet-500 text-white hover:bg-violet-600 font-bold" data-testid="qst-analyze-btn">
          {isRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Analyser le questionnaire
        </Button>
      </div>
    );
  }

  const cfg = tierConfig(analysis.qualification);
  const score = analysis.score || 0;
  const completion = analysis.completion_rate || 0;
  const isFallback = analysis.source === "fallback" || analysis.model === "heuristic";

  return (
    <div className={`rounded-2xl border ${cfg.border} ${cfg.bg} p-5 relative overflow-hidden`} data-testid={testId}>
      <div className={`absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gradient-to-br ${cfg.gradient} opacity-10 blur-2xl pointer-events-none`} />

      <div className="flex items-start justify-between gap-3 flex-wrap relative">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${cfg.gradient} flex items-center justify-center text-2xl shadow-lg`}>
            <ClipboardList className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className={`text-2xl font-black ${cfg.text}`} data-testid="qst-score">
              {score}
              <span className="text-base text-slate-400 font-medium"> /100</span>
            </div>
            <div className={`text-xs font-bold ${cfg.text} tracking-wider`}>
              QUALIFICATION {cfg.label}
              <span className="text-slate-500 font-normal"> · confiance {Math.round((analysis.confidence || 0) * 100)}%</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Complétion</div>
          <div className="text-lg font-bold text-white">{completion}%</div>
          <div className="text-[10px] text-slate-500">{analysis.fields_answered}/{analysis.fields_visible} champs</div>
        </div>
        <Button onClick={handle} disabled={isRunning} variant="outline" size="sm" className="border-white/15 text-slate-300 hover:bg-white/5 text-xs" data-testid="qst-reanalyze-btn">
          {isRunning ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1.5" />}
          Réanalyser
        </Button>
      </div>

      {analysis.verdict && (
        <div className="mt-4 text-base font-semibold text-white leading-snug" data-testid="qst-verdict">
          « {analysis.verdict} »
        </div>
      )}
      {analysis.summary && !compact && (
        <p className="mt-2 text-sm text-slate-300 leading-relaxed" data-testid="qst-summary">
          {analysis.summary}
        </p>
      )}

      {!compact && (
        <div className="grid md:grid-cols-3 gap-3 mt-5">
          {(analysis.strengths || []).length > 0 && (
            <div className="bg-black/20 rounded-xl p-3 border border-white/5" data-testid="qst-strengths">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400 mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" /> Points forts
              </div>
              <ul className="space-y-1.5">
                {analysis.strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-400" />
                    <span className="leading-snug">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(analysis.gaps || []).length > 0 && (
            <div className="bg-black/20 rounded-xl p-3 border border-white/5" data-testid="qst-gaps">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-400 mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Lacunes
              </div>
              <ul className="space-y-1.5">
                {analysis.gaps.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400" />
                    <span className="leading-snug">{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(analysis.red_flags || []).length > 0 && (
            <div className="bg-black/20 rounded-xl p-3 border border-rose-500/15" data-testid="qst-red-flags">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-rose-400 mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Signaux négatifs
              </div>
              <ul className="space-y-1.5">
                {analysis.red_flags.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-rose-400" />
                    <span className="leading-snug">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {analysis.recommended_action && (
        <div className="mt-4 pt-3 border-t border-white/5 flex items-start gap-2" data-testid="qst-recommended-action">
          <Target className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400 mb-1">Action recommandée</div>
            <div className="text-sm text-slate-200">{analysis.recommended_action}</div>
          </div>
        </div>
      )}

      {analysis.recommended_status && (
        <div className="mt-2 text-xs text-slate-400">
          <span className="text-slate-500">Statut recommandé :</span>{" "}
          <span className="font-medium text-slate-200">{analysis.recommended_status}</span>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[10px] text-slate-500">
        <span>{isFallback ? "⚙️ Analyse heuristique" : `🤖 ${analysis.model || "Gemini"}`} · {analysis.questionnaire_name}</span>
        {analysis.analyzed_at && <span>analysé le {formatDate(analysis.analyzed_at)}</span>}
      </div>
    </div>
  );
}
